<script setup lang="ts">
import MoneyAmount from '@/app/components/MoneyAmount.vue'
import {
  AccountIcon,
  ActionIcon,
  activateRowPrimary,
  EmptyState,
  ErrorState,
  NavIcon,
  Skeleton,
} from '@taler/ui'
import { type Locale, type Money } from '@taler/domain'
import { plainAmount, useAccountingView } from '@/app/accounting-view'
import { computed, ref } from 'vue'
import { useI18n } from 'vue-i18n'
import { RouterLink } from 'vue-router'
import { type AccountRow, homeCurrencyOf } from '../internal/account-tree.ts'
import AccountActionsMenu from '@/features/accounts/components/AccountActionsMenu.vue'

const props = defineProps<{
  rows: readonly AccountRow[]
  balances: ReadonlyMap<string, Money>
  locale: Locale
  canViewBalances: boolean
  canCreate: boolean
  canEdit: boolean
  canHide: boolean
  canArchive: boolean
  canReconcile: boolean
  canSetDefault: boolean
  isBusy: boolean
  defaultAccountId?: string | undefined
  isLoading: boolean
  error: Error | null
  retrying?: boolean | undefined
}>()

const emit = defineEmits<{
  retry: []
  'add-child': [parentId: string]
  edit: [id: string]
  hide: [id: string]
  unhide: [id: string]
  'archive-request': [id: string]
  restore: [id: string]
  reconcile: []
  'set-default': [id: string]
}>()

/** Account name, type, holds, balance, then the row menu. */
const columnCount = computed(() => (props.canViewBalances ? 3 : 2))

const { t } = useI18n()

/** Every account of a class, grouped under its root row - the root becomes the section
 * header rather than a data row of its own (AGENTS.md-style report §5: "promote the root
 * row into the section header, zero extra rows, no badge, no box"). */
const { accountingView } = useAccountingView()

const sections = computed(() => {
  const out: { klass: string; header: AccountRow; body: AccountRow[] }[] = []
  for (const row of props.rows) {
    if (row.depth === 0) out.push({ klass: row.accountClass, header: row, body: [] })
    else out[out.length - 1]?.body.push(row)
  }
  return out
})

/** A balance is a level, never a flow: unsigned, tinted only when negative - never green for
 * "positive", which trains the eye to ignore colour (AGENTS.md, visual hierarchy rule 4). */
const collapsedClasses = ref<ReadonlySet<string>>(new Set())

function toggleSection(klass: string): void {
  const next = new Set(collapsedClasses.value)
  if (next.has(klass)) next.delete(klass)
  else next.add(klass)
  collapsedClasses.value = next
}

function tone(value: Money): 'negative' | undefined {
  return value.units < 0n ? 'negative' : undefined
}

function shown(klass: string, value: Money): Money {
  return plainAmount(value, klass, accountingView.value)
}

/** Each depth step is 20px; the elbow sits in the gutter just before the icon it leads to. */
function indentStyle(row: AccountRow): { paddingLeft: string } {
  if (row.depth === 1) return { paddingLeft: 'var(--space-cell)' }
  return { paddingLeft: `calc(${(row.depth - 1) * 20 - 10}px + var(--space-cell))` }
}

function balanceOf(row: AccountRow): Money | undefined {
  if (homeCurrencyOf(row) === undefined) return undefined
  const balance = props.balances.get(row.id)
  return balance === undefined ? undefined : shown(row.accountClass, balance)
}
</script>

<template>
  <div>
    <ul
      v-if="isLoading"
      aria-hidden="true"
      class="flex flex-col gap-[var(--space-inline)] p-[var(--space-cell)]"
    >
      <li v-for="index in 8" :key="index"><Skeleton height="1.5rem" /></li>
    </ul>
    <p v-if="isLoading" class="sr-only" role="status">{{ t('accounts.loading') }}</p>

    <ErrorState
      v-else-if="error"
      :title="t('accounts.errorTitle')"
      :body="t('accounts.errorBody')"
      :retry-label="t('accounts.retry')"
      :retrying="retrying"
      @retry="emit('retry')"
    />

    <EmptyState v-else-if="sections.length === 0" :title="t('accounts.empty')">
      <template #icon><NavIcon name="accounts" /></template>
    </EmptyState>

    <div
      v-else
      role="region"
      tabindex="0"
      :aria-label="t('accounts.tableLabel')"
      class="overflow-x-auto rounded-[var(--radius-surface)] border border-[color:var(--color-border)] bg-[color:var(--color-surface)]"
    >
      <table class="w-full border-collapse">
        <thead>
          <tr class="table-head">
            <th class="whitespace-nowrap px-[var(--space-cell)] py-[var(--space-inline)]" scope="col">
              {{ t('accounts.columnAccount') }}
            </th>
            <th
              v-if="canViewBalances"
              class="w-[16rem] whitespace-nowrap px-[var(--space-cell)] py-[var(--space-inline)] text-right"
              scope="col"
            >
              {{ t('accounts.columnBalance') }}
            </th>
            <th class="w-12 px-[var(--space-cell)] py-[var(--space-inline)]" scope="col">
              <span class="sr-only">{{ t('accounts.columnActions') }}</span>
            </th>
          </tr>
        </thead>

        <template v-for="section in sections" :key="section.klass">
          <tbody>
            <tr
              class="border-b border-[color:var(--color-border)]"
              :style="{ backgroundColor: `${section.header.color}14` }"
            >
              <th
                :colspan="columnCount"
                scope="rowgroup"
                class="px-[var(--space-cell)] py-[var(--space-inline)] text-left"
              >
                <button
                  type="button"
                  class="flex items-center gap-[var(--space-inline)] rounded-[var(--radius-control)] outline-none"
                  :aria-expanded="!collapsedClasses.has(section.klass)"
                  @click="toggleSection(section.klass)"
                >
                  <svg
                    width="16"
                    height="16"
                    viewBox="0 0 16 16"
                    aria-hidden="true"
                    class="shrink-0 text-[color:var(--color-text-muted)] transition-transform duration-[var(--motion-fast)]"
                    :class="collapsedClasses.has(section.klass) ? '-rotate-90' : ''"
                    fill="none"
                    stroke="currentColor"
                    stroke-width="1.7"
                  >
                    <path d="M4 6l4 4 4-4" />
                  </svg>
                  <span class="text-[length:var(--text-step-5)] font-semibold text-[color:var(--color-text)]">
                    {{ section.header.label }}
                  </span>
                  <span
                    class="whitespace-nowrap rounded-full bg-[color:var(--color-surface)] px-[var(--space-inline)] text-[length:var(--text-step-1)] text-[color:var(--color-text-muted)]"
                  >
                    {{ t('accounts.accountCount', { count: section.body.length }) }}
                  </span>
                </button>
              </th>
            </tr>

            <template v-if="!collapsedClasses.has(section.klass)">
              <tr
                v-for="row in section.body"
                :key="row.id"
                class="table-row cursor-pointer border-b border-[color:var(--color-border)] last:border-0"
                @click="activateRowPrimary"
              >
                <td class="p-0">
                  <RouterLink
                    :to="{ name: 'account-detail', params: { accountId: row.id } }"
                    data-row-primary
                    class="flex min-h-[var(--density-row)] w-full items-center gap-[var(--space-inline)] px-[var(--space-cell)] text-left outline-none"
                    :style="indentStyle(row)"
                  >
                    <span
                      v-if="row.depth > 1"
                      aria-hidden="true"
                      class="h-5 w-[10px] shrink-0 rounded-bl-[var(--radius-control)] border-b border-l border-[color:var(--color-border-strong)]"
                    />
                    <span
                      aria-hidden="true"
                      class="flex h-7 w-7 shrink-0 items-center justify-center rounded-[var(--radius-control)]"
                      :style="{ backgroundColor: `${row.color}22`, color: row.color }"
                    >
                      <AccountIcon :icon="row.icon" label="" />
                    </span>
                    <span
                      class="truncate text-[length:var(--text-step-3)] font-medium text-[color:var(--color-text)]"
                      >{{ row.label }}</span
                    >
                    <span
                      v-if="row.childCount > 0"
                      class="shrink-0 whitespace-nowrap rounded-full bg-[color:var(--color-surface-sunken)] px-[var(--space-inline)] text-[length:var(--text-step-1)] text-[color:var(--color-text-muted)]"
                      >{{ t('accounts.subAccountCount', row.childCount) }}</span
                    >
                    <span
                      v-if="row.id === defaultAccountId"
                      :title="t('accounts.defaultAccountNote')"
                      class="shrink-0 text-[color:var(--color-attention)]"
                    >
                      <ActionIcon name="starFilled" />
                      <span class="sr-only">{{ t('accounts.defaultAccountNote') }}</span>
                    </span>
                    <span
                      v-if="row.isArchived"
                      class="shrink-0 whitespace-nowrap text-[length:var(--text-step-1)] font-semibold uppercase tracking-[var(--tracking-caps)] text-[color:var(--color-attention)]"
                      >{{ t('accounts.archived') }}</span
                    >
                    <span
                      v-if="row.isHidden"
                      class="shrink-0 whitespace-nowrap text-[length:var(--text-step-1)] font-semibold uppercase tracking-[var(--tracking-caps)] text-[color:var(--color-text-muted)]"
                      >{{ t('accounts.hidden') }}</span
                    >
                  </RouterLink>
                </td>
                <td v-if="canViewBalances" class="px-[var(--space-cell)] py-[var(--space-inline)] text-right">
                  <MoneyAmount v-if="balanceOf(row)" :value="balanceOf(row)!" :tone="tone(balanceOf(row)!)" />
                </td>
                <td class="h-[var(--density-row)] w-px px-[var(--space-cell)] align-middle">
                  <AccountActionsMenu
                    :row="row"
                    :can-archive="canArchive"
                    :can-create="canCreate"
                    :can-edit="canEdit"
                    :can-hide="canHide"
                    :can-reconcile="canReconcile"
                    :can-set-default="canSetDefault"
                    :is-default="row.id === defaultAccountId"
                    :is-busy="isBusy"
                    show-open
                    @add-child="emit('add-child', $event)"
                    @edit="emit('edit', $event)"
                    @hide="emit('hide', $event)"
                    @unhide="emit('unhide', $event)"
                    @archive-request="emit('archive-request', $event)"
                    @restore="emit('restore', $event)"
                    @reconcile="emit('reconcile')"
                    @set-default="emit('set-default', $event)"
                  />
                </td>
              </tr>
            </template>
          </tbody>
        </template>
      </table>
    </div>
  </div>
</template>
